import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import { Row, Col, FormGroup, Label, Input, Card, CardHeader, CardBody } from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import { useTranslation } from "react-i18next";
import { useUpdateCustomer } from "api/customers/service";
import { formatPriceWithDollar } from "utils/currency";
import { formatDateForPicker, formatDateForAPI } from "utils/dateUtils";
import { formatPhoneNumber } from "utils/phoneUtils";
import Select from "components/Select";
import StatesInput from "components/StatesInput";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const DetailedBorrowerForm = forwardRef(({
  customerData,
  onUpdate,
  showValidation = true,
  title = "Detailed Borrower Information",
  asCoBorrower = false,
  activeColor = "blue",
  isTheme2 = false,
}, ref) => {
  const { t } = useTranslation();
  const updateCustomer = useUpdateCustomer();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const inputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0, fontSize: "14px" };
  const dateInputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0 };


  const [formData, setFormData] = useState({
    ssn: "",
    full_name: "",
    date_of_birth: "",
    gender: "",
    dl: "",
    active_service_member: false,
    dl_state: null,
    dl_exp: "",
    email: "",
    home_phone: "",
    home_phone_2: "",
    cell_phone: "",
    pager: "",
    current_address: {
      address_1: "",
      address_2: "",
      city: "",
      county: "",
      state: "",
      zip_code: "",
      country: "US",
      residence_type: "",
      years_at: "",
      months_at: "",
      residence_payment: "",
      lien_holder: "",
      lien_holder_phone: ""
    },
    current_employment: {
      employer: "",
      position: "",
      type: "",
      income: "",
      supervisor_phone: "",
      address_1: "",
      city: "",
      state: "",
      zip_code: "",
      years_at: "",
      months_at: ""
    }
  });

  // Populate form data when customerData changes
  useEffect(() => {
    if (customerData) {
      setFormData({
        ssn: customerData.ssn || "",
        full_name: customerData.full_name || "",
        date_of_birth: customerData.date_of_birth || "",
        gender: customerData.gender || "",
        dl: customerData.dl || "",
        active_service_member: customerData.active_service_member || false,
        dl_state: customerData.dl_state,
        dl_exp: customerData.dl_exp || "",
        email: customerData.email || "",
        home_phone: customerData.home_phone || "",
        home_phone_2: customerData.home_phone_2 || "",
        cell_phone: customerData.cell_phone || "",
        pager: customerData.pager || "",
        current_address: {
          address_1: customerData.current_address?.address_1 || "",
          address_2: customerData.current_address?.address_2 || "",
          city: customerData.current_address?.city || "",
          county: customerData.current_address?.county || "",
          state: customerData.current_address?.state || "",
          zip_code: customerData.current_address?.zip_code || "",
          country: customerData.current_address?.country || "US",
          residence_type: customerData.current_address?.residence_type || "",
          years_at: customerData.current_address?.years_at || "",
          months_at: customerData.current_address?.months_at || "",
          residence_payment: customerData.current_address?.residence_payment ? formatPriceWithDollar(customerData.current_address?.residence_payment) : "",
          lien_holder: customerData.current_address?.lien_holder || "",
          lien_holder_phone: customerData.current_address?.lien_holder_phone || ""
        },
        current_employment: {
          employer: customerData.current_employment?.employer || "",
          position: customerData.current_employment?.position || "",
          type: customerData.current_employment?.type || "",
          income: customerData.current_employment?.income || "",
          supervisor_phone: customerData.current_employment?.supervisor_phone || "",
          address_1: customerData.current_employment?.address_1 || "",
          city: customerData.current_employment?.city || "",
          state: customerData.current_employment?.state || "",
          zip_code: customerData.current_employment?.zip_code || "",
          years_at: customerData.current_employment?.years_at || "",
          months_at: customerData.current_employment?.months_at || ""
        }
      });
    }
  }, [customerData]);

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Special handler for phone number fields with formatting
  const handlePhoneChange = (field, value) => {
    const formattedValue = formatPhoneNumber(value);
    handleChange(field, formattedValue);
  };

  // Helper function to safely check if a field has a value (handles Date objects, strings, null, undefined)
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (value instanceof Date) return true;
    if (typeof value === 'string') return value.trim() !== '';
    return Boolean(value);
  };

  const handleSubmit = async () => {
    const customerId = customerData?.customer_id;
    if (!customerId) return true;

    // Normalize date fields to API format (YYYY-MM-DD)
    const payload = {
      ...formData,
      date_of_birth: formatDateForAPI(formData.date_of_birth),
      dl_exp: formatDateForAPI(formData.dl_exp)
    };

    try {
      await updateCustomer.mutateAsync({
        id: customerId,
        data: payload,
        // custom flag for API layer if needed
        asCoBorrower
      });

      if (onUpdate) {
        onUpdate(formData);
      }
      return true;
    } catch (error) {
      console.error('Error updating customer:', error);
      return false;
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    state: {
      formData
    },
    updateCustomer: async () => {
      try {
        await handleSubmit();
        return true;
      } catch (error) {
        return false;
      }
    },
    isValidated: async () => {
      // Identity Information validation
      const isIdentityValid =
        hasValue(formData.full_name) &&
        hasValue(formData.ssn) &&
        hasValue(formData.date_of_birth);

      // Contact Information validation (only Home Phone)
      const isContactValid =
        hasValue(formData.home_phone);

      // Current Address validation
      const isCurrentAddressValid =
        hasValue(formData.current_address.address_1) &&
        hasValue(formData.current_address.city) &&
        hasValue(formData.current_address.county) &&
        hasValue(formData.current_address.state) &&
        hasValue(formData.current_address.zip_code) &&
        hasValue(formData.current_address.residence_type) &&
        (hasValue(formData.current_address.years_at) || hasValue(formData.current_address.months_at));

      // Current Employment validation
      const isCurrentEmploymentValid =
        hasValue(formData.current_employment.type) &&
        hasValue(formData.current_employment.employer) &&
        (hasValue(formData.current_employment.years_at) || hasValue(formData.current_employment.months_at)) &&
        hasValue(formData.current_employment.income) &&
        hasValue(formData.current_employment.supervisor_phone);

      const isValid = isIdentityValid && isContactValid && isCurrentAddressValid && isCurrentEmploymentValid;


      if (!isValid) {
        return false;
      }

      // If valid, submit the form
      const submitResult = await handleSubmit();
      return submitResult;
    }
  }));

  // Identity Information validation
  const isIdentityValid =
    hasValue(formData.full_name) &&
    hasValue(formData.ssn) &&
    hasValue(formData.date_of_birth);

  // Contact Information validation (only Home Phone)
  const isContactValid =
    hasValue(formData.home_phone);

  // Current Address validation
  const isCurrentAddressValid =
    hasValue(formData.current_address.address_1) &&
    hasValue(formData.current_address.city) &&
    hasValue(formData.current_address.county) &&
    hasValue(formData.current_address.state) &&
    hasValue(formData.current_address.zip_code) &&
    hasValue(formData.current_address.residence_type) &&
    (hasValue(formData.current_address.years_at) || hasValue(formData.current_address.months_at));

  // Current Employment validation
  const isCurrentEmploymentValid =
    hasValue(formData.current_employment.type) &&
    hasValue(formData.current_employment.employer) &&
    (hasValue(formData.current_employment.years_at) || hasValue(formData.current_employment.months_at)) &&
    hasValue(formData.current_employment.income) &&
    hasValue(formData.current_employment.supervisor_phone);

  const isValid = isIdentityValid && isContactValid && isCurrentAddressValid && isCurrentEmploymentValid;

  // Get missing required fields for validation message
  const getMissingFields = () => {
    const missing = [];
    if (!hasValue(formData.full_name)) missing.push(t('detailedBorrowerForm.fullName'));
    if (!hasValue(formData.ssn)) missing.push(t('detailedBorrowerForm.ssn'));
    if (!hasValue(formData.date_of_birth)) missing.push(t('detailedBorrowerForm.dateOfBirth'));
    if (!hasValue(formData.home_phone)) missing.push(t('detailedBorrowerForm.homePhone'));
    if (!hasValue(formData.current_address.address_1)) missing.push(t('detailedBorrowerForm.residenceAddress'));
    if (!hasValue(formData.current_address.city)) missing.push(t('detailedBorrowerForm.residenceCity'));
    if (!hasValue(formData.current_address.county)) missing.push(t('detailedBorrowerForm.residenceCounty'));
    if (!hasValue(formData.current_address.state)) missing.push(t('detailedBorrowerForm.residenceState'));
    if (!hasValue(formData.current_address.zip_code)) missing.push(t('detailedBorrowerForm.residenceZipCode'));
    if (!hasValue(formData.current_address.residence_type)) missing.push(t('detailedBorrowerForm.residenceType'));
    if (!hasValue(formData.current_address.years_at) && !hasValue(formData.current_address.months_at)) missing.push(t('detailedBorrowerForm.timeAtAddress'));
    if (!hasValue(formData.current_employment.type)) missing.push(t('detailedBorrowerForm.employmentType'));
    if (!hasValue(formData.current_employment.employer)) missing.push(t('detailedBorrowerForm.employerName'));
    if (!hasValue(formData.current_employment.years_at) && !hasValue(formData.current_employment.months_at)) missing.push(t('detailedBorrowerForm.timeOnJob'));
    if (!hasValue(formData.current_employment.income)) missing.push(t('detailedBorrowerForm.monthlyIncome'));
    if (!hasValue(formData.current_employment.supervisor_phone)) missing.push(t('detailedBorrowerForm.employmentPhone'));
    return missing;
  };

  const missingFields = getMissingFields();

  return (
    <div className="card">
      <div className="card-body mt-0">
        <CardHeader className="font-weight-bold pl-0">{t('detailedBorrowerForm.identityInformation')}</CardHeader>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="full_name">{t('detailedBorrowerForm.fullName')}</Label>
              <Input
                id="full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterFullName')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="ssn">{t('detailedBorrowerForm.ssn')}</Label>
              <Input
                id="ssn"
                type="text"
                value={formData.ssn}
                onChange={(e) => handleChange("ssn", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterSSN')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="date_of_birth">{t('detailedBorrowerForm.dateOfBirth')}</Label>
              <ReactDatetime
                inputProps={{
                  className: "form-control",
                  placeholder: t('detailedBorrowerForm.selectDate'),
                  style: dateInputStyle
                }}
                value={formData.date_of_birth ? formatDateForPicker(formData.date_of_birth) : formData.date_of_birth}
                onChange={(date) => handleChange("date_of_birth", date)}
                dateFormat="MM-DD-YYYY"
                timeFormat={false}
                closeOnSelect={true}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for='gender'>{t('detailedBorrowerForm.gender')}</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('detailedBorrowerForm.selectGender')}
                name="gender"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={formData.gender ? { value: formData.gender, label: formData.gender === 'Male' ? t('detailedBorrowerForm.male') : t('detailedBorrowerForm.female') } : null}
                onChange={(option) => handleChange('gender', option?.value || '')}
                options={[
                  { value: 'Male', label: t('detailedBorrowerForm.male') },
                  { value: 'Female', label: t('detailedBorrowerForm.female') }
                ]}
                isClearable
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.active_service_member}
                  onChange={(e) => handleChange("active_service_member", e.target.checked)}
                />
                <span className="form-check-sign" />
                {t('detailedBorrowerForm.activeServiceMember')}
              </Label>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="dl">{t('detailedBorrowerForm.dl')}</Label>
              <Input
                id="dl"
                type="text"
                value={formData.dl}
                onChange={(e) => handleChange("dl", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterDLNumber')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="dl_state">DL State</Label>
              <StatesInput
                styles={selectStyles}
                name="dl_state"
                value={formData.dl_state}
                onChange={(stateObject) => {
                  handleChange('dl_state', stateObject ? { id: stateObject.id, abbreviation: stateObject.abbreviation } : null);
                }}
                placeholder="Select State"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="dl_exp">DL Expiration</Label>
              <ReactDatetime
                inputProps={{
                  className: "form-control",
                  placeholder: t('detailedBorrowerForm.selectDate'),
                  style: dateInputStyle
                }}
                value={formData.dl_exp ? formatDateForPicker(formData.dl_exp) : formData.dl_exp}
                onChange={(date) => handleChange("dl_exp", date)}
                dateFormat="MM-DD-YYYY"
                timeFormat={false}
                closeOnSelect={true}
              />
            </FormGroup>
          </Col>
        </Row>
        {showValidation && !isIdentityValid && (
          <Row>
            <Col md="12">
              <div className="alert alert-warning mb-0 rounded-0">
                <strong>{t('detailedBorrowerForm.identityInformationRequired')}</strong> {
                  [
                    !hasValue(formData.full_name) && t('detailedBorrowerForm.fullName'),
                    !hasValue(formData.ssn) && t('detailedBorrowerForm.ssn'),
                    !hasValue(formData.date_of_birth) && t('detailedBorrowerForm.dateOfBirth')
                  ].filter(Boolean).join(" | ")
                }
              </div>
            </Col>
          </Row>
        )}

        <CardHeader className="font-weight-bold pl-0">{t('detailedBorrowerForm.contactInformation')}</CardHeader>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for='email'>{t('detailedBorrowerForm.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterEmail')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for='cell_phone'>{t('detailedBorrowerForm.cellPhone')}</Label>
              <Input
                id="cell_phone"
                type="tel"
                value={formData.cell_phone}
                onChange={(e) => handlePhoneChange("cell_phone", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.cellPhonePlaceholder')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for='home_phone'>{t('detailedBorrowerForm.homePhone')}</Label>
              <Input
                id="home_phone"
                type="tel"
                value={formData.home_phone}
                onChange={(e) => handlePhoneChange("home_phone", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.homePhonePlaceholder')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="home_phone_2">Home Phone 2</Label>
              <Input
                id="home_phone_2"
                type="tel"
                value={formData.home_phone_2}
                onChange={(e) => handlePhoneChange("home_phone_2", e.target.value)}
                className="form-control"
                placeholder="Home Phone 2 (e.g., +1-555-123-4567)"
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="pager">Pager</Label>
              <Input
                id="pager"
                type="tel"
                value={formData.pager}
                onChange={(e) => handlePhoneChange("pager", e.target.value)}
                className="form-control"
                placeholder="Pager (e.g., +1-555-123-4567)"
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        {showValidation && !isContactValid && (
          <Row>
            <Col md="12">
              <div className="alert alert-warning mb-0 rounded-0">
                <strong>{t('detailedBorrowerForm.contactInformationRequired')}</strong> {
                  [
                    !hasValue(formData.home_phone) && t('detailedBorrowerForm.homePhone')
                  ].filter(Boolean).join(" | ")
                }
              </div>
            </Col>
          </Row>
        )}

        {/* Current Address */}
        <CardHeader className="font-weight-bold pl-0">{t('detailedBorrowerForm.currentAddress')}</CardHeader>
        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="current_address.address_1">{t('detailedBorrowerForm.address1')}</Label>
              <Input
                id="current_address.address_1"
                type="text"
                value={formData.current_address.address_1}
                onChange={(e) => handleChange("current_address.address_1", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterAddress1')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="current_address.address_2">{t('detailedBorrowerForm.address2')}</Label>
              <Input
                id="current_address.address_2"
                type="text"
                value={formData.current_address.address_2}
                onChange={(e) => handleChange("current_address.address_2", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterAddress2')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="3">
            <FormGroup>
              <Label for="current_address.city">{t('detailedBorrowerForm.city')}</Label>
              <Input
                id="current_address.city"
                type="text"
                value={formData.current_address.city}
                onChange={(e) => handleChange("current_address.city", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterCity')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="current_address.county">{t('detailedBorrowerForm.county')}</Label>
              <Input
                id="current_address.county"
                type="text"
                value={formData.current_address.county}
                onChange={(e) => handleChange("current_address.county", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterCounty')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="current_address.state">{t('detailedBorrowerForm.state')}</Label>
              <StatesInput
                styles={selectStyles}
                name="current_address.state"
                value={formData.current_address.state}
                onChange={(stateObject) => {
                  handleChange('current_address.state', stateObject ? { id: stateObject.id, abbreviation: stateObject.abbreviation } : null);
                }}
                placeholder="Select State"
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="current_address.zip_code">{t('detailedBorrowerForm.zipCode')}</Label>
              <Input
                id="current_address.zip_code"
                type="text"
                value={formData.current_address.zip_code}
                onChange={(e) => handleChange("current_address.zip_code", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterZipCode')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <FormGroup>
              <Label for="current_address.residence_type">{t('detailedBorrowerForm.residenceType')}</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('detailedBorrowerForm.selectResidenceType')}
                name="current_address.residence_type"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={formData.current_address.residence_type ? {
                  value: formData.current_address.residence_type,
                  label: formData.current_address.residence_type === "rent" ? t('detailedBorrowerForm.rent') :
                    formData.current_address.residence_type === "own-mortgage" ? t('detailedBorrowerForm.ownMortgage') :
                      formData.current_address.residence_type === "own-no-mortgage" ? t('detailedBorrowerForm.ownNoMortgage') :
                        formData.current_address.residence_type === "living-with-other" ? t('detailedBorrowerForm.livingWithOther') : formData.current_address.residence_type
                } : null}
                onChange={(option) => handleChange("current_address.residence_type", option?.value || '')}
                options={[
                  { value: "rent", label: t('detailedBorrowerForm.rent') },
                  { value: "own-mortgage", label: t('detailedBorrowerForm.ownMortgage') },
                  { value: "own-no-mortgage", label: t('detailedBorrowerForm.ownNoMortgage') },
                  { value: "living-with-other", label: t('detailedBorrowerForm.livingWithOther') }
                ]}
                isClearable
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="current_address.years_at">{t('detailedBorrowerForm.yearsAtAddress')}</Label>
              <Input
                id="current_address.years_at"
                type="number"
                value={formData.current_address.years_at}
                onChange={(e) => handleChange("current_address.years_at", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.years')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="current_address.months_at">{t('detailedBorrowerForm.monthsAtAddress')}</Label>
              <Input
                id="current_address.months_at"
                type="number"
                value={formData.current_address.months_at}
                onChange={(e) => handleChange("current_address.months_at", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.months')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="3">
            <FormGroup>
              <Label for='current_address.country'>{t('detailedBorrowerForm.country')}</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('detailedBorrowerForm.country')}
                name="current_address.country"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={formData.current_address.country ? {
                  value: formData.current_address.country,
                  label: formData.current_address.country === 'US' ? t('detailedBorrowerForm.unitedStates') : formData.current_address.country === 'CA' ? t('detailedBorrowerForm.canada') : t('detailedBorrowerForm.mexico')
                } : null}
                onChange={(option) => handleChange('current_address.country', option?.value || '')}
                options={[
                  { value: 'US', label: t('detailedBorrowerForm.unitedStates') },
                  { value: 'CA', label: t('detailedBorrowerForm.canada') },
                  { value: 'MX', label: t('detailedBorrowerForm.mexico') }
                ]}
                isClearable
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for='current_address.residence_payment'>{t('detailedBorrowerForm.payment')}</Label>
              <Input
                id="current_address.residence_payment"
                type="text"
                value={formData.current_address.residence_payment}
                onChange={(e) => {
                  const raw = String(e.target.value || "").replace(/[^\d.]/g, "");
                  handleChange("current_address.residence_payment", raw);
                }}
                onBlur={() =>
                  handleChange(
                    "current_address.residence_payment",
                    formatPriceWithDollar(formData.current_address?.residence_payment)
                  )
                }
                onFocus={() => {
                  const raw = String(formData.current_address?.residence_payment || "").replace(/[^\d.]/g, "");
                  handleChange("current_address.residence_payment", raw);
                }}
                className="form-control"
                placeholder="$0.00"
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for='current_address.lien_holder'>{t('detailedBorrowerForm.landlordName')}</Label>
              <Input
                id="current_address.lien_holder"
                type="text"
                value={formData.current_address.lien_holder}
                onChange={(e) => handleChange("current_address.lien_holder", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterLandlordName')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for='current_address.lien_holder_phone'>{t('detailedBorrowerForm.landlordPhone')}</Label>
              <Input
                id="current_address.lien_holder_phone"
                type="tel"
                value={formData.current_address.lien_holder_phone}
                onChange={(e) => handlePhoneChange("current_address.lien_holder_phone", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.landlordPhonePlaceholder')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        {showValidation && !isCurrentAddressValid && (
          <Row>
            <Col md="12">
              <div className="alert alert-warning mb-0 rounded-0">
                <strong>{t('detailedBorrowerForm.currentAddressRequired')}</strong> {
                  [
                    !hasValue(formData.current_address.address_1) && t('detailedBorrowerForm.residenceAddress'),
                    !hasValue(formData.current_address.city) && t('detailedBorrowerForm.residenceCity'),
                    !hasValue(formData.current_address.county) && t('detailedBorrowerForm.residenceCounty'),
                    !hasValue(formData.current_address.state) && t('detailedBorrowerForm.residenceState'),
                    !hasValue(formData.current_address.zip_code) && t('detailedBorrowerForm.residenceZipCode'),
                    !hasValue(formData.current_address.residence_type) && t('detailedBorrowerForm.residenceType'),
                    (!hasValue(formData.current_address.years_at) && !hasValue(formData.current_address.months_at)) && t('detailedBorrowerForm.timeAtAddress')
                  ].filter(Boolean).join(" | ")
                }
              </div>
            </Col>
          </Row>
        )}
        <CardHeader className="font-weight-bold pl-0">{t('detailedBorrowerForm.currentEmployment')}</CardHeader>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="current_employment.type">{t('detailedBorrowerForm.employmentType')}</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder={t('detailedBorrowerForm.selectEmploymentType')}
                name="current_employment.type"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={formData.current_employment.type ? {
                  value: formData.current_employment.type,
                  label: formData.current_employment.type
                } : null}
                onChange={(option) => handleChange('current_employment.type', option?.value || '')}
                options={[
                  { value: 'Full Time', label: t('detailedBorrowerForm.fullTime') },
                  { value: 'Part Time', label: t('detailedBorrowerForm.partTime') },
                  { value: 'Self Employed', label: t('detailedBorrowerForm.selfEmployed') },
                  { value: 'Contract', label: t('detailedBorrowerForm.contract') },
                  { value: 'Unemployed', label: t('detailedBorrowerForm.unemployed') },
                  { value: 'Retired', label: t('detailedBorrowerForm.retired') }
                ]}
                isClearable
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="current_employment.employer">{t('detailedBorrowerForm.employerName')}</Label>
              <Input
                id="current_employment.employer"
                type="text"
                value={formData.current_employment.employer}
                onChange={(e) => handleChange("current_employment.employer", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterEmployerName')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <FormGroup>
              <Label for='current_employment.years_at'>{t('detailedBorrowerForm.yearsOnJob')}</Label>
              <Input
                id="current_employment.years_at"
                type="number"
                value={formData.current_employment.years_at}
                onChange={(e) => handleChange("current_employment.years_at", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.years')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for='current_employment.months_at'>{t('detailedBorrowerForm.monthsOnJob')}</Label>
              <Input
                id="current_employment.months_at"
                type="number"
                value={formData.current_employment.months_at}
                onChange={(e) => handleChange("current_employment.months_at", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.months')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for='current_employment.income'>{t('detailedBorrowerForm.monthlyIncome')}</Label>
              <Input
                id="current_employment.income"
                type="text"
                value={formData.current_employment.income}
                onChange={(e) => handleChange("current_employment.income", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterMonthlyIncome')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for='current_employment.supervisor_phone'>{t('detailedBorrowerForm.employmentPhone')}</Label>
              <Input
                id="current_employment.supervisor_phone"
                type="tel"
                value={formData.current_employment.supervisor_phone}
                onChange={(e) => handlePhoneChange("current_employment.supervisor_phone", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.employmentPhonePlaceholder')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for='current_employment.position'>{t('detailedBorrowerForm.position')}</Label>
              <Input
                id="current_employment.position"
                type="text"
                value={formData.current_employment.position}
                onChange={(e) => handleChange("current_employment.position", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterPosition')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="current_employment.address_1">{t('detailedBorrowerForm.workAddress')}</Label>
              <Input
                id="current_employment.address_1"
                type="text"
                value={formData.current_employment.address_1}
                onChange={(e) => handleChange("current_employment.address_1", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterWorkAddress')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <FormGroup>
              <Label for="current_employment.city">{t('detailedBorrowerForm.city')}</Label>
              <Input
                id="current_employment.city"
                type="text"
                value={formData.current_employment.city}
                onChange={(e) => handleChange("current_employment.city", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterCity')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="current_employment.state">{t('detailedBorrowerForm.state')}</Label>
              <StatesInput
                styles={selectStyles}
                name="current_employment.state"
                value={formData.current_employment.state}
                onChange={(stateObject) => {
                  handleChange('current_employment.state', stateObject ? { id: stateObject.id, abbreviation: stateObject.abbreviation } : null);
                }}
                placeholder="Select State"
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="current_employment.zip_code">{t('detailedBorrowerForm.zipCode')}</Label>
              <Input
                id="current_employment.zip_code"
                type="text"
                value={formData.current_employment.zip_code}
                onChange={(e) => handleChange("current_employment.zip_code", e.target.value)}
                className="form-control"
                placeholder={t('detailedBorrowerForm.enterZipCode')}
                style={inputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
        {showValidation && !isCurrentEmploymentValid && (
          <Row>
            <Col md="12">
              <div className="alert alert-warning mb-0 rounded-0">
                <strong>{t('detailedBorrowerForm.currentEmploymentRequired')}</strong> {
                  [
                    !hasValue(formData.current_employment.type) && t('detailedBorrowerForm.employmentType'),
                    !hasValue(formData.current_employment.employer) && t('detailedBorrowerForm.employerName'),
                    (!hasValue(formData.current_employment.years_at) && !hasValue(formData.current_employment.months_at)) && t('detailedBorrowerForm.timeOnJob'),
                    !hasValue(formData.current_employment.income) && t('detailedBorrowerForm.monthlyIncome'),
                    !hasValue(formData.current_employment.supervisor_phone) && t('detailedBorrowerForm.employmentPhone')
                  ].filter(Boolean).join(" | ")
                }
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
});

export default DetailedBorrowerForm;
